import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import baseUrl from "../../../../../api/baseUrl";
import "./DeliveryDetailsModal.scss";
import PersonIcon from "@mui/icons-material/Person";
import SearchIcon from "@mui/icons-material/Search";
import InputText from "../../../../../components/inputs/InputText";
import PhoneNumberInput from "../../../../../components/inputs/PhoneNumberInput";
import ModelEmpty from "../../../../../components/commonModal/ModelEmpty";

export interface DeliveryDetails {
  customerId: number | null;
  customerName: string;
  deliveryPhoneNumber: string;
  deliveryAddress: string;
  deliveryNote: string;
}

export interface DeliveryPaymentOverride {
  cash: number;
  card: number;
  bank: number;
}

interface DeliveryDetailsModalProps {
  open: boolean;
  setOpen: (value: boolean) => void;
  onConfirm: (
    details: DeliveryDetails,
    payNow: boolean,
    paymentOverride?: DeliveryPaymentOverride,
  ) => void;
  currentCash?: number;
  currentCard?: number;
  currentBank?: number;
  currencySymbol?: string;
  netTotal?: number;
}

const DeliveryDetailsModal: React.FC<DeliveryDetailsModalProps> = ({
  open,
  setOpen,
  onConfirm,
  currentCash = 0,
  currentCard = 0,
  currentBank = 0,
  currencySymbol = "",
  netTotal = 0,
}) => {
  const [mode, setMode] = useState<"quick" | "existing">("quick");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [address, setAddress] = useState("");
  const [note, setNote] = useState("");
  const [customerName, setCustomerName] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(
    null,
  );

  const [searchQuery, setSearchQuery] = useState("");
  const [customers, setCustomers] = useState<any[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<any[]>([]);

  // Quick-entry payment fields — only used when nothing was already
  // entered in the main POS checkout panel before opening this modal
  const [payCash, setPayCash] = useState("");
  const [payCard, setPayCard] = useState("");
  const [payBank, setPayBank] = useState("");

  const existingPaymentTotal =
    Number(currentCash || 0) + Number(currentCard || 0) + Number(currentBank || 0);
  const hasExistingPayment = existingPaymentTotal > 0;
  const quickEntryTotal =
    Number(payCash || 0) + Number(payCard || 0) + Number(payBank || 0);
  const effectivePaymentTotal = hasExistingPayment
    ? existingPaymentTotal
    : quickEntryTotal;
  const payNowShortfall = Number(netTotal || 0) - effectivePaymentTotal;
  const payNowInsufficient = payNowShortfall > 0;

  useEffect(() => {
    if (!open) {
      setMode("quick");
      setPhoneNumber("");
      setAddress("");
      setNote("");
      setCustomerName("");
      setSelectedCustomerId(null);
      setSearchQuery("");
      setPayCash("");
      setPayCard("");
      setPayBank("");
      return;
    }
    baseUrl
      .get("/branch/customer/get-customers")
      .then((res) => {
        const data = res.data?.data || [];
        setCustomers(data);
        setFilteredCustomers(data);
      })
      .catch(() => {
        setCustomers([]);
        setFilteredCustomers([]);
      });
  }, [open]);

  useEffect(() => {
    if (searchQuery.trim() === "") {
      setFilteredCustomers(customers);
    } else {
      setFilteredCustomers(
        customers.filter(
          (c: any) =>
            c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            c.phoneNumber?.includes(searchQuery),
        ),
      );
    }
  }, [searchQuery, customers]);

  const handleSelectExistingCustomer = (customer: any) => {
    setSelectedCustomerId(customer.id);
    setCustomerName(customer.name);
    setPhoneNumber(customer.phoneNumber);
  };

  const handleSubmit = (payNow: boolean) => {
    if (!phoneNumber.trim()) {
      toast.error("Phone number is required for delivery orders");
      return;
    }

    let paymentOverride: DeliveryPaymentOverride | undefined;
    if (payNow) {
      if (!hasExistingPayment) {
        const cash = Number(payCash || 0);
        const card = Number(payCard || 0);
        const bank = Number(payBank || 0);
        if (cash + card + bank <= 0) {
          toast.error("Please enter a payment amount (cash, card, or bank)");
          return;
        }
        paymentOverride = { cash, card, bank };
      }

      if (payNowInsufficient) {
        toast.error(
          "Payment amount must be greater than or equal to net total",
        );
        return;
      }
    }

    onConfirm(
      {
        customerId: selectedCustomerId,
        customerName: customerName.trim(),
        deliveryPhoneNumber: phoneNumber.trim(),
        deliveryAddress: address.trim(),
        deliveryNote: note.trim(),
      },
      payNow,
      paymentOverride,
    );
  };

  if (!open) return null;

  return (
    <ModelEmpty
      open={open}
      setopen={setOpen}
      className="delivery_details_modal"
      title="Delivery Details"
    >
      <div className="delivery_details_body">
        <div className="delivery_mode_tabs">
          <button
            type="button"
            className={`mode_tab ${mode === "quick" ? "active" : ""}`}
            onClick={() => setMode("quick")}
          >
            Quick Entry
          </button>
          <button
            type="button"
            className={`mode_tab ${mode === "existing" ? "active" : ""}`}
            onClick={() => setMode("existing")}
          >
            Pick Existing Customer
          </button>
        </div>

        {mode === "existing" && (
          <>
            <div className="search_input_wrapper">
              <SearchIcon className="search_icon" />
              <input
                type="text"
                className="search_input"
                placeholder="Search by name or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>

            <div className="customers_container">
              {filteredCustomers.length > 0 ? (
                filteredCustomers.map((c: any) => (
                  <div
                    key={c.id}
                    className={`customer_item ${
                      selectedCustomerId === c.id ? "selected" : ""
                    }`}
                    onClick={() => handleSelectExistingCustomer(c)}
                  >
                    <div className="customer_info">
                      <div className="customer_name">
                        <PersonIcon />
                        {c.name}
                      </div>
                      <div className="customer_phone">{c.phoneNumber}</div>
                    </div>
                    <button
                      type="button"
                      className="btn_select"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectExistingCustomer(c);
                      }}
                    >
                      Select
                    </button>
                  </div>
                ))
              ) : (
                <div className="empty_state">
                  <PersonIcon />
                  <p>No customers found.</p>
                </div>
              )}
            </div>
          </>
        )}

        <div className="form_grid">
          <div className="form_group">
            <PhoneNumberInput
              required
              value={phoneNumber}
              onChange={(e: any) => setPhoneNumber(e.target.value)}
              label="Phone Number"
              name="phoneNumber"
              placeholder="Required"
            />
          </div>
          <div className="form_group">
            <InputText
              name="customerName"
              label="Customer Name (optional)"
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </div>
          <div className="form__group__textarea">
            <label className="form__label">Delivery Address (optional)</label>
            <textarea
              className="form__field"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              rows={2}
            />
          </div>
          <div className="form_group">
            <InputText
              name="note"
              label="Note (optional)"
              type="text"
              placeholder="Landmark, instructions, etc."
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </div>
        </div>

        <div className="delivery_payment_section">
          <div className="payment_section_label">
            Payment (for Pay Now) — Net Total: {currencySymbol}{" "}
            {Number(netTotal || 0).toFixed(2)}
          </div>
          {hasExistingPayment ? (
            <div className="payment_existing_badge">
              Already entered in POS — Cash: {currencySymbol}{" "}
              {Number(currentCash).toFixed(2)}, Card: {currencySymbol}{" "}
              {Number(currentCard).toFixed(2)}, Bank: {currencySymbol}{" "}
              {Number(currentBank).toFixed(2)}
            </div>
          ) : (
            <div className="form_grid three_col">
              <div className="form_group">
                <InputText
                  name="payCash"
                  label="Cash"
                  type="number"
                  value={payCash}
                  onChange={(e) => setPayCash(e.target.value)}
                />
              </div>
              <div className="form_group">
                <InputText
                  name="payCard"
                  label="Card"
                  type="number"
                  value={payCard}
                  onChange={(e) => setPayCard(e.target.value)}
                />
              </div>
              <div className="form_group">
                <InputText
                  name="payBank"
                  label="Bank"
                  type="number"
                  value={payBank}
                  onChange={(e) => setPayBank(e.target.value)}
                />
              </div>
            </div>
          )}
          {payNowInsufficient && (
            <div className="payment_shortfall_warning">
              Payment must be greater than or equal to net total — short by{" "}
              {currencySymbol} {payNowShortfall.toFixed(2)}
            </div>
          )}
        </div>

        <div className="add_form_footer">
          <button
            type="button"
            className="btn_cancel"
            onClick={() => handleSubmit(false)}
          >
            Send Order (Pay Later)
          </button>
          <button
            type="button"
            className="btn_submit"
            disabled={payNowInsufficient}
            title={
              payNowInsufficient
                ? "Payment amount must be greater than or equal to net total"
                : undefined
            }
            onClick={() => handleSubmit(true)}
          >
            Pay Now
          </button>
        </div>
      </div>
    </ModelEmpty>
  );
};

export default DeliveryDetailsModal;
